import React, { useState, useEffect, useRef } from "react";
import { Router } from "@abyss/web/ui/Router";
import { Heading } from "@abyss/web/ui/Heading";
import { PlanSection } from "@src/components/PlanSection/PlanSection";
import { Breadcrumb } from "@src/common/Breadcrumb/Breadcrumb";
import { Benefits } from "@src/common/Benefits/Benefits";
import { IconSymbol } from "@abyss/web/ui/IconSymbol";
import { V2Button } from "@abyss/web/ui/Button";
import { Popover } from "@abyss/web/ui/Popover";
import { PlanNav } from "@src/components/PlanNav";
import { AddItems } from "@src/components/AddItems";
import { PlanNavMobile } from "@src/components/PlanNavMobile/PlanNavMobile";

import planinfo from "@src/data/plandetails.json";
import styles from "./plandetail.module.css";

import classNames from "classnames";

export const PlanDetail = ({ data = planinfo }) => {
  const [activeSection, setActiveSection] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const ref = useRef(null);
  const sections = useRef([]);
  const barRef = useRef(null);

  const handleScroll = () => {
    const pageYOffset = window.scrollY;
    let newActiveSection = null;

    sections.current.forEach((section) => {
      const sectionOffsetTop = section.offsetTop - 150;
      const sectionHeight = section.offsetHeight;

      if (
        pageYOffset >= sectionOffsetTop &&
        pageYOffset < sectionOffsetTop + sectionHeight
      ) {
        newActiveSection = section.id;
      }
    });

    setActiveSection(newActiveSection);
  };

  useEffect(() => {
    const header = barRef?.current;
    const observer = new IntersectionObserver(
      ([e]) => {
        setIsSticky(e.isIntersecting);
      },
      { rootMargin: "-1px 0px 0px 0px", threshold: [1] }
    );

    if (header) {
      observer.observe(header);
    }

    // clean up the observer
    return () => {
      observer.unobserve(header);
    };
  }, [barRef]);

  useEffect(() => {
    sections.current = document.querySelectorAll("[data-section]");
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <React.Fragment>
      <Router.MetaTags title="Plan Detail" />

      <section className={styles.head}>
        <div>
          <Breadcrumb
            linkText="View all Medicare Advantage plans"
            mobileText="View all plans"
            location="55430 Hennepin County"
          />

          <div className={styles.header}>
            <Heading.Level>
              <Heading>{data.plan.title}</Heading>
            </Heading.Level>

            <div className={styles.headerButtons}>
              <div>
                <V2Button variant="filled" className={styles.orange}>
                  Enroll
                </V2Button>
              </div>
              <div>
                <V2Button variant="outline">
                  <IconSymbol
                    icon="favorite"
                    color="#002677"
                    variant="outlined"
                    size="24px"
                  />
                  Save
                </V2Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className={styles.bannerWrap}>
          <div className={styles.banner}>
            <div className={styles.bannerWrap}>
              <ul className={classNames(styles.bannerTop, styles.bannerList)}>
                <li>
                  <div className={styles.bannerTitle}>Monthly premium</div>
                  <div className={styles.bannerCopy}>
                    {data.plan.monthlyPremium}
                  </div>
                </li>
                <li>
                  <div className={styles.bannerTitle}>
                    Medicare Part B premium giveback{" "}
                    <Popover
                      title="Concise and clear heading"
                      content="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer vitae molestie risus, ut semper mi."
                    >
                      <IconSymbol
                        icon="info"
                        color="$primary1"
                        size="22px"
                        variant="outlined"
                      />
                    </Popover>
                  </div>
                  <div className={styles.bannerCopy}>{data.plan.giveback}</div>
                </li>
                <li>
                  <div className={styles.bannerTitle}>
                    Out-of-pocket maximum{" "}
                    <Popover
                      title="Concise and clear heading"
                      content="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer vitae molestie risus, ut semper mi."
                    >
                      <IconSymbol
                        icon="info"
                        color="$primary1"
                        size="22px"
                        variant="outlined"
                      />
                    </Popover>
                  </div>
                  <div className={styles.bannerAlign}>
                    <div className={styles.bannerCopy}>
                      <span>{data.plan.oopmin}</span>
                    </div>
                    <div className={styles.bannerCopyDef}>In-network</div>
                  </div>
                  <div className={styles.bannerAlign}>
                    <div className={styles.bannerCopy}>
                      <span>{data.plan.oopmout}</span>
                    </div>
                    <div className={styles.bannerCopyDef}>Out-of-network</div>
                  </div>
                </li>
                <li>
                  <div className={styles.bannerTitle}>
                    Annual medical deductible{" "}
                    <Popover
                      title="Concise and clear heading"
                      content="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer vitae molestie risus, ut semper mi."
                    >
                      <IconSymbol
                        icon="info"
                        color="$primary1"
                        size="22px"
                        variant="outlined"
                      />
                    </Popover>
                  </div>
                  <div className={styles.bannerCopy}>$0</div>
                </li>
              </ul>
              <ul
                className={classNames(styles.bannerBottom, styles.bannerList)}
              >
                <li>
                  <div className={styles.bannerTitle}>
                    Primary care provider (PCP)
                  </div>
                  <div className={styles.bannerCopy}>
                    {data.plan.pcp} <span>copay</span>
                  </div>
                </li>
                <li>
                  <div className={styles.bannerTitle}>
                    Care while traveling{" "}
                    <Popover
                      title="Concise and clear heading"
                      content="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer vitae molestie risus, ut semper mi."
                    >
                      <IconSymbol
                        icon="info"
                        color="$primary1"
                        size="22px"
                        variant="outlined"
                      />
                    </Popover>
                  </div>
                  <div className={styles.bannerCopyNone}>
                    Covered with UnitedHealthcare Medicare National Network
                  </div>
                </li>
                <li>
                  <div className={styles.bannerTitle}>
                    Estimated annual drug cost
                  </div>
                  <div className={styles.bannerButton}>
                    <AddItems type="estimated" />
                  </div>
                </li>
              </ul>
            </div>
            <div className={styles.bannerEnd}>
              <div>
                <V2Button variant="filled" className={styles.orange}>
                  Enroll
                </V2Button>
              </div>
              <div>
                <V2Button variant="outline">
                  <IconSymbol
                    icon="favorite"
                    color="#002677"
                    variant="outlined"
                    size="24px"
                  />
                  Saved
                </V2Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <Benefits title="Discover the benefits" items={data.plan.benefits} />
      </section>

      <section className={styles.wrapNoPad}>
        <div className={styles.barRef}>
          <div className={isSticky ? styles.stickyFade : ""} />
        </div>
        <div className={styles.planListing}>
          <div className={styles.mobileBox}>
            <div className={styles.planListingTitle}>
              <Heading>Explore the plan details</Heading>
            </div>
          </div>
          <div className={styles.mobileNav}>
            <PlanNavMobile
              items={data.plan.sections}
              activeScroll={activeSection}
            />
          </div>
          <div className={styles.planSide} ref={barRef}>
            <div className={styles.desktopBox}>
              <div className={styles.planListingTitle}>
                <Heading>Explore the plan details</Heading>
              </div>
            </div>
            <div className={styles.desktopNav}>
              <PlanNav
                items={data.plan.sections}
                activeScroll={activeSection}
              />
            </div>
          </div>
          <div className={styles.planRight}>
            {data.plan.sections.map((section, index) => {
              return <PlanSection items={section} layout key={index} />;
            })}
          </div>
        </div>
      </section>
    </React.Fragment>
  );
};
